import { Link } from "react-router-dom";
import { ArrowLeft, Target, Leaf, Users, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO = "https://customer-assets.emergentagent.com/job_8a27fd21-2917-4128-b502-8e2ad7b4f186/artifacts/rr8eu6u8_EC%20full%20-%20without%20border%20%281%29.png";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="ElectroCycle Logo" className="w-[110px] md:w-[150px] h-auto object-contain" style={{ border: 'none', outline: 'none' }} />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-gradient-to-b from-[#D2E4D6]/30 to-white">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#3F3F3F] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            ABOUT ELECTROCYCLE
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-gray-600">
            Zambia's trusted partner for responsible Waste Electrical and Electronic Equipment (WEEE) management.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#3F3F3F] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Who We Are
          </h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            ElectroCycle is a Producer Responsibility Organisation (PRO) dedicated to providing end-to-end WEEE management solutions for businesses across Zambia. We work with electronics manufacturers, importers, retailers, and corporations to ensure their electronic waste is collected, processed, and recycled in compliance with environmental regulations.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Based in Kitwe, we provide nationwide e-waste management services across Zambia, combining local expertise with international best practices to deliver reliable, transparent, and environmentally sound solutions.
          </p>
        </div>
      </section>

      {/* Our Role */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#3F3F3F] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Our Role in WEEE Management
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8">
            As a PRO, ElectroCycle takes on the responsibility of managing electronic waste on behalf of producers and businesses. We handle the entire lifecycle of WEEE—from collection and logistics to recycling and reporting—so our partners can focus on their core business while meeting their Extended Producer Responsibility (EPR) obligations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-sm border border-gray-100">
              <div className="w-12 h-12 rounded-sm bg-[#D2E4D6] flex items-center justify-center text-[#056839] mb-4">
                <Recycle size={24} />
              </div>
              <h3 className="font-semibold text-[#3F3F3F] mb-2">Collection & Recycling</h3>
              <p className="text-gray-600 text-sm">Systematic collection and environmentally sound processing of electronic waste.</p>
            </div>
            <div className="bg-white p-6 rounded-sm border border-gray-100">
              <div className="w-12 h-12 rounded-sm bg-[#D2E4D6] flex items-center justify-center text-[#056839] mb-4">
                <Leaf size={24} />
              </div>
              <h3 className="font-semibold text-[#3F3F3F] mb-2">Environmental Compliance</h3>
              <p className="text-gray-600 text-sm">Ensuring all processes meet regulatory requirements and environmental standards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Impact */}
      <section className="py-16">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#3F3F3F] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Our Mission & Impact
          </h2>
          
          <div className="bg-[#056839] text-white p-8 rounded-sm mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-sm bg-[#69A488] flex items-center justify-center flex-shrink-0">
                <Target size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-xl mb-2">Our Mission</h3>
                <p className="text-[#D2E4D6] leading-relaxed">
                  To lead the responsible management of electronic waste in Zambia, protecting the environment and public health while supporting businesses in meeting their sustainability goals and regulatory obligations.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-[#D2E4D6] flex items-center justify-center text-[#056839] mx-auto mb-4">
                <Leaf size={28} />
              </div>
              <h3 className="font-semibold text-[#3F3F3F] mb-2">Environmental Protection</h3>
              <p className="text-gray-600 text-sm">Preventing hazardous materials from entering landfills and waterways.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-[#D2E4D6] flex items-center justify-center text-[#056839] mx-auto mb-4">
                <Recycle size={28} />
              </div>
              <h3 className="font-semibold text-[#3F3F3F] mb-2">Resource Recovery</h3>
              <p className="text-gray-600 text-sm">Extracting valuable materials for reuse in the circular economy.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-[#D2E4D6] flex items-center justify-center text-[#056839] mx-auto mb-4">
                <Users size={28} />
              </div>
              <h3 className="font-semibold text-[#3F3F3F] mb-2">Community Benefit</h3>
              <p className="text-gray-600 text-sm">Creating local jobs and promoting environmental awareness.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#3F3F3F] mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
            LEADERSHIP
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Photo */}
            <div className="flex justify-center md:justify-end">
              <img 
                src="https://customer-assets.emergentagent.com/job_8a27fd21-2917-4128-b502-8e2ad7b4f186/artifacts/qi9gh3s1_image.png" 
                alt="Chrispin Kapelwa - Managing Director"
                className="w-64 h-64 md:w-72 md:h-72 object-cover rounded-full shadow-lg border-4 border-[#D2E4D6]"
              />
            </div>
            
            {/* Bio */}
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-semibold text-[#3F3F3F] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                Chrispin Kapelwa
              </h3>
              <p className="text-[#056839] font-medium mb-4">Managing Director</p>
              <p className="text-gray-600 leading-relaxed">
                Chrispin Kapelwa is the Managing Director of ElectroCycle Zambia, with over nine years of experience in electronic waste management and circular economy initiatives. He has led the company since 2016, overseeing strategic development, operations, and large-scale collection and recycling efforts. With a strong background in physics, chemistry, and climate change, he brings both technical expertise and leadership to advancing sustainable waste solutions in Zambia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-16 bg-[#E4D5B7]">
        <div className="px-6 md:px-12 lg:px-24 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#3F3F3F] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Partner With Us
          </h2>
          <p className="text-gray-600 mb-8">
            Ready to manage your electronic waste responsibly? Get in touch with our team.
          </p>
          <Link to="/#contact">
            <Button className="bg-[#056839] text-white hover:bg-[#04502b] rounded-sm px-8 py-3 font-semibold">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3F3F3F] text-white py-8">
        <div className="px-6 md:px-12 lg:px-24 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} ElectroCycle Zambia. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
